// Admin → Languages. The registry behind the i18n resolution chain
// (motiflink §1.5). Chrome is English-only by owner ruling (CLAUDE.md §5).
//
// Every guard the API enforces is mirrored here as a disabled control with a
// reason, so the UI never offers an action that comes back 400. The API stays
// the real enforcement — this is courtesy, not security.

import { Button, Dialog, DialogContent, Input } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { DataTable, Icon, PageHeader, StatusBadge, actionsColumn, type Column } from '~/components';
import { ApiError } from '~/lib/api';
import {
  createLanguage,
  deleteLanguage,
  listLanguages,
  reorderLanguages,
  setDefaultLanguage,
  updateLanguage,
  type LanguageRow,
} from '~/lib/i18n';

export const Route = createFileRoute('/_authed/languages')({
  component: LanguagesPage,
});

const QK = ['i18n', 'languages'] as const;

function LanguagesPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<LanguageRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LanguageRow | null>(null);

  const languages = useQuery({
    queryKey: QK,
    queryFn: ({ signal }) => listLanguages(signal),
    staleTime: 30_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: QK });
  const onError = (e: unknown) =>
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');

  const setDefault = useMutation({
    mutationFn: setDefaultLanguage,
    onSuccess: invalidate,
    onError,
  });
  const toggleActive = useMutation({
    mutationFn: (row: LanguageRow) => updateLanguage(row.id, { isActive: !row.isActive }),
    onSuccess: invalidate,
    onError,
  });
  const reorder = useMutation({
    mutationFn: reorderLanguages,
    onSuccess: invalidate,
    onError,
  });
  const remove = useMutation({
    mutationFn: deleteLanguage,
    onSuccess: () => {
      setConfirmDelete(null);
      void invalidate();
    },
    onError,
  });

  const rows = languages.data ?? [];

  // Swap two neighbours and send the whole recomputed order — the API applies
  // it as one atomic batch, so a partial write can't scramble the list.
  function move(index: number, direction: -1 | 1) {
    const next = [...rows];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    reorder.mutate(next.map((row, i) => ({ id: row.id, sortOrder: i })));
  }

  const columns: Column<LanguageRow>[] = [
    {
      key: 'order',
      header: 'Order',
      alwaysVisible: true,
      width: '6rem',
      render: (row) => {
        const i = rows.indexOf(row);
        return (
          // Buttons, not drag-and-drop: keyboard-accessible for free, and this
          // list is two rows long today.
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <IconButton
              label={`Move ${row.name} up`}
              disabled={i === 0 || reorder.isPending}
              onClick={() => move(i, -1)}
            >
              <Icon name="chevronDown" className="h-3.5 w-3.5 rotate-180" />
            </IconButton>
            <IconButton
              label={`Move ${row.name} down`}
              disabled={i === rows.length - 1 || reorder.isPending}
              onClick={() => move(i, 1)}
            >
              <Icon name="chevronDown" className="h-3.5 w-3.5" />
            </IconButton>
          </div>
        );
      },
    },
    {
      key: 'code',
      header: 'Code',
      render: (row) => <span className="font-mono text-admin-900">{row.code}</span>,
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className="font-medium text-admin-900">{row.name}</span>,
    },
    {
      key: 'nativeName',
      header: 'Native',
      render: (row) => (
        // The native name is real content in its own script, so it carries its
        // own direction even though the console chrome is LTR.
        <span className="text-admin-800" dir={row.direction}>
          {row.nativeName}
        </span>
      ),
    },
    {
      key: 'direction',
      header: 'Dir',
      hideBelow: 'md',
      render: (row) => <span className="uppercase text-admin-500">{row.direction}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1.5">
          {row.isDefault && <StatusBadge status="default" label="Default" tone="info" />}
          <StatusBadge
            status={row.isActive ? 'active' : 'archived'}
            label={row.isActive ? 'Active' : 'Inactive'}
            dot
          />
        </div>
      ),
    },
    // Every guard the API enforces is mirrored as a disabled item WITH a
    // reason, so the console never offers an action that returns 400.
    actionsColumn<LanguageRow>({
      label: (row) => row.name,
      primary: (row) => ({
        id: 'edit',
        label: 'Edit',
        icon: 'edit',
        onSelect: () => setEditing(row),
      }),
      actions: (row) => [
        {
          id: 'default',
          label: 'Make default',
          icon: 'checkCircle',
          onSelect: () => setDefault.mutate(row.id),
          disabled: row.isDefault || !row.isActive || setDefault.isPending,
          disabledReason: row.isDefault
            ? 'This is already the default language.'
            : 'An inactive language cannot be made the default.',
        },
        {
          id: 'active',
          label: row.isActive ? 'Deactivate' : 'Activate',
          icon: row.isActive ? 'eye' : 'checkCircle',
          onSelect: () => toggleActive.mutate(row),
          disabled: row.isDefault || toggleActive.isPending,
          disabledReason: 'The default language must stay active.',
        },
        {
          id: 'delete',
          label: 'Delete language',
          icon: 'trash',
          destructive: true,
          onSelect: () => setConfirmDelete(row),
          disabled: row.isDefault,
          disabledReason: 'The default language cannot be deleted.',
        },
      ],
    }),
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Languages"
        description="The languages the platform offers. Order here is the order visitors see in the switcher."
        actions={
          <Button onClick={() => setEditing(NEW_LANGUAGE)}>
            <Icon name="plus" className="h-4 w-4" />
            Add language
          </Button>
        }
      />

      {error && (
        <p role="alert" className="mb-4 rounded-xl bg-error-50 px-4 py-3 text-sm text-error-700">
          {error}
        </p>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={languages.isLoading}
        error={languages.error}
        onRetry={() => void languages.refetch()}
        caption="Languages"
        skeletonRows={2}
        empty={{
          icon: 'languages',
          title: 'No languages yet',
          description: 'Add the first language to start offering the platform in it.',
          action: <Button onClick={() => setEditing(NEW_LANGUAGE)}>Add language</Button>,
        }}
      />

      {editing && (
        <LanguageForm
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
          onError={onError}
        />
      )}

      {confirmDelete && (
        <ConfirmDelete
          row={confirmDelete}
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove.mutate(confirmDelete.id)}
        />
      )}
    </div>
  );
}

// A sentinel rather than `null`, so the form can be one component for both
// create and edit without a separate "mode" prop.
const NEW_LANGUAGE = { id: '' } as LanguageRow;

function LanguageForm({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: LanguageRow;
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const isNew = row.id === '';
  const [code, setCode] = useState(row.code ?? '');
  const [name, setName] = useState(row.name ?? '');
  const [nativeName, setNativeName] = useState(row.nativeName ?? '');
  const [direction, setDirection] = useState<'ltr' | 'rtl'>(row.direction ?? 'ltr');

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? createLanguage({
            code: code.trim(),
            name: name.trim(),
            nativeName: nativeName.trim(),
            direction,
          })
        : updateLanguage(row.id, {
            name: name.trim(),
            nativeName: nativeName.trim(),
            direction,
          }),
    onSuccess: onSaved,
    onError,
  });

  const valid =
    name.trim() !== '' &&
    nativeName.trim() !== '' &&
    (!isNew || /^[a-z][a-z0-9-]*$/.test(code.trim()));

  return (
    <Overlay onClose={onClose} title={isNew ? 'Add language' : `Edit ${row.name}`}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) save.mutate();
        }}
      >
        <label className="block text-sm font-medium text-ink-700">
          Code
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase())}
            placeholder="fr"
            disabled={!isNew}
            className="mt-1 font-mono disabled:bg-admin-50"
          />
          <span className="mt-1 block text-xs text-ink-500">
            {isNew
              ? 'Lowercase letters, digits and dashes. Cannot be changed later.'
              : 'Immutable — translation rows reference this code.'}
          </span>
        </label>

        <label className="block text-sm font-medium text-ink-700">
          Name (English)
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="French"
            className="mt-1"
          />
        </label>

        <label className="block text-sm font-medium text-ink-700">
          Native name
          <Input
            value={nativeName}
            onChange={(e) => setNativeName(e.target.value)}
            placeholder="Français"
            dir={direction}
            className="mt-1"
          />
          <span className="mt-1 block text-xs text-ink-500">
            Shown in the language switcher, in the language itself.
          </span>
        </label>

        <fieldset>
          <legend className="text-sm font-medium text-ink-700">Direction</legend>
          <div className="mt-1 flex gap-4">
            {(['ltr', 'rtl'] as const).map((d) => (
              <label key={d} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="radio"
                  name="direction"
                  value={d}
                  checked={direction === d}
                  onChange={() => setDirection(d)}
                />
                {d.toUpperCase()}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!valid || save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Add language' : 'Save changes'}
          </Button>
        </div>
      </form>
    </Overlay>
  );
}

function ConfirmDelete({
  row,
  pending,
  onCancel,
  onConfirm,
}: {
  row: LanguageRow;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Overlay onClose={onCancel} title={`Delete ${row.name}?`}>
      <p className="text-sm text-ink-700">
        This also deletes every translation override stored for{' '}
        <span className="font-mono">{row.code}</span> — the database cascades them. Strings that
        ship in the code bundles are unaffected.
      </p>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={pending} onClick={onConfirm}>
          {pending ? 'Deleting…' : 'Delete language'}
        </Button>
      </div>
    </Overlay>
  );
}

// ─── small shared bits ──────────────────────────────────────────────────────

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded-lg border border-admin-200 text-admin-600 transition-colors hover:bg-admin-100 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

// Radix under the hood — focus trap, Escape, scroll lock and restore-focus are
// all things a hand-rolled overlay gets subtly wrong.
function Overlay({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={title} className="bg-white">
        {children}
      </DialogContent>
    </Dialog>
  );
}
